import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { adminFetchIdDocument, adminLogin } from '@/lib/api';

export function AdminIdPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [phase, setPhase] = useState<'auth' | 'loading' | 'show' | 'denied'>('loading');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<{
    status: string | null;
    verifiedAt: string | null;
    links: Array<{ label: string; url: string; expiresAt: string }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * `afterLogin` distinguishes the first unauthenticated load (where showing the
   * password form is the whole point) from a 401 that arrives immediately after
   * a successful login. The latter means the session cookie was never stored, and
   * without a message the identical form just re-renders and nothing appears to happen.
   */
  async function load(afterLogin = false) {
    setBusy(true);
    setError(null);
    try {
      const r = await adminFetchIdDocument(userId);
      setData(r);
      setPhase('show');
    } catch (e) {
      const msg = String((e as Error).message ?? e);
      if (msg === 'admin_unauthenticated') {
        setPhase('auth');
        if (afterLogin) {
          setError(
            'Password accepted, but the session cookie was not stored. The admin cookie is SameSite=Strict, so the API has to be served from this same domain rather than the Render URL directly.'
          );
        }
      } else if (msg === 'user_not_found') {
        setError('User not found.');
        setPhase('denied');
      } else if (msg === 'stripe_identity_key_required') {
        setError(
          'Stripe Identity access is not configured. Set STRIPE_IDENTITY_SECRET_KEY on the backend.'
        );
        setPhase('denied');
      } else if (msg === 'stripe_file_write_permission_required') {
        setError(
          'Stripe Identity access is configured, but the restricted key is missing Files write permission. Add Files > Write to STRIPE_IDENTITY_SECRET_KEY.'
        );
        setPhase('denied');
      } else if (msg === 'stripe_identity_results_permission_required') {
        setError(
          'Stripe Identity access is configured, but the restricted key cannot access detailed verification result files. Add Identity > All detailed verification results to STRIPE_IDENTITY_SECRET_KEY and make sure its IP allowlist includes this backend.'
        );
        setPhase('denied');
      } else {
        setError(msg);
        setPhase('denied');
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  async function submitLogin(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminLogin(password);
      await load(true);
    } catch (err) {
      setError(String((err as Error).message ?? err));
      setBusy(false);
    }
  }

  return (
    <Section eyebrow="Admin" title="Government ID">
      <div className="grid max-w-xl gap-4">
        <p className="text-sm text-bone/60">User ID: {userId}</p>

        {phase === 'auth' && (
          <form
            onSubmit={submitLogin}
            className="grid gap-3 rounded-lg border border-bone/15 bg-neutral-900/40 p-4"
          >
            <p className="text-sm">Enter the admin password to view this ID.</p>
            <label className="block">
              <span className="mb-1 block text-xs uppercase tracking-wider text-bone/60">
                Password
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                autoFocus
              />
            </label>
            <button type="submit" disabled={busy} className="btn-primary w-full sm:w-auto">
              {busy ? 'Checking…' : 'Sign in'}
            </button>
            {error && (
              <div
                role="alert"
                className="rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
              >
                {error}
              </div>
            )}
          </form>
        )}

        {phase === 'show' && data && (
          <div className="grid gap-3">
            <p className="text-sm">
              Status: <strong>{data.status ?? 'unverified'}</strong>
              {data.verifiedAt ? ` · verified ${data.verifiedAt}` : ''}
            </p>
            {data.links.length === 0 ? (
              <p className="text-sm text-bone/60">No verified ID document on file.</p>
            ) : (
              // Render inline. Stripe serves these file links as attachments, so
              // an anchor just downloads the file; an <img> shows it in place.
              <div className="grid gap-4">
                {data.links.map((l) => (
                  <figure
                    key={l.label}
                    className="rounded border border-bone/15 bg-neutral-900 p-3"
                    data-testid="id-document"
                  >
                    <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-sm">
                      <span>{l.label}</span>
                      <span className="text-xs text-bone/50">expires {l.expiresAt}</span>
                    </figcaption>
                    <img
                      src={l.url}
                      alt={l.label}
                      className="max-h-[70vh] w-full rounded object-contain"
                    />
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs text-bone/50 underline hover:text-accent"
                    >
                      Open full size
                    </a>
                  </figure>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => load()}
              className="btn-ghost w-full sm:w-auto"
            >
              Refresh links
            </button>
          </div>
        )}

        {phase === 'loading' && <p className="text-sm text-bone/60">Loading…</p>}
        {phase === 'denied' && error && (
          <div
            role="alert"
            className="rounded border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
          >
            {error}
          </div>
        )}
      </div>
    </Section>
  );
}
